import { useEffect, useState } from 'react';
import {
  remoteIssueTypeSchema,
  ruleVocabularySchema,
  type LineItemRule,
  type LineItemTarget,
  type RuleVocabulary,
  type LineItemTargetOption,
  type RemoteIssueType,
  type SetLineItemTarget,
} from '@forecastify/shared';
import { Button, Icon, Select, Switch } from '@forecastify/ui';
import { useT } from '../../i18n';
import { PanelField } from './setting-row';
import { ProviderLogo } from '../integrations/provider-logo';
import { useSession } from '../../session';
import { RuleEditor, type RuleOptions } from './rule-editor';

/**
 * ⚠️ **An Epic, by its name.** The port carries no hierarchy level, and the hint below is advice
 * rather than a gate — a false negative costs one sentence, a false positive one sentence too. HU
 * Jira sites call it *Eposz*.
 */
const EPIC = /epic|eposz/iu;

/**
 * ux-pass-2 · the issue types the target's remote project can create, read with the instance's
 * credential (`GET /integrations/:id/remote-projects/:remoteProjectId/issue-types`).
 *
 * ⚠️ **`null` is "not loaded", `'failed'` is "asked and could not"**, so the picker can say which —
 * an empty list would read as a project with no types at all.
 */
function useIssueTypes(
  target: LineItemTarget | null,
  enabled: boolean,
): readonly RemoteIssueType[] | null | 'failed' {
  const session = useSession();
  const [types, setTypes] = useState<readonly RemoteIssueType[] | null | 'failed'>(null);
  const instanceId = target?.instanceId ?? null;
  const remoteProjectId = target?.remoteProjectId ?? null;

  useEffect(() => {
    if (!enabled || instanceId === null || remoteProjectId === null) return;
    /* An object, so the closure reads it rather than a narrowed `true`. */
    const live = { current: true };
    setTypes(null);
    void (async () => {
      const response = await session
        .request(
          `/integrations/${encodeURIComponent(instanceId)}/remote-projects/${encodeURIComponent(
            remoteProjectId,
          )}/issue-types`,
        )
        .catch(() => null);
      const body: unknown = response?.ok === true ? await response.json().catch(() => null) : null;
      const parsed = remoteIssueTypeSchema
        .array()
        .safeParse((body as { data?: unknown } | null)?.data);
      if (live.current) setTypes(parsed.success ? parsed.data : 'failed');
    })();
    return () => {
      live.current = false;
    };
  }, [session, enabled, instanceId, remoteProjectId]);

  return types;
}

/**
 * 4 Nov 2026 · the Jira lists of a remote project only PICKED — what the draft rule below the
 * pickers offers before *Mentés* (`GET /integrations/:id/remote-projects/:id/vocabulary`). `null`
 * until it lands, and an empty one when it cannot: the free-text fields work either way.
 */
function usePickedVocabulary(
  instanceId: string,
  remoteProjectId: string,
  enabled: boolean,
): RuleVocabulary | null {
  const session = useSession();
  const [vocabulary, setVocabulary] = useState<RuleVocabulary | null>(null);

  useEffect(() => {
    setVocabulary(null);
    if (!enabled || instanceId === '' || remoteProjectId === '') return;
    const live = { current: true };
    void (async () => {
      const response = await session
        .request(
          `/integrations/${encodeURIComponent(instanceId)}/remote-projects/${encodeURIComponent(
            remoteProjectId,
          )}/vocabulary`,
        )
        .catch(() => null);
      const body: unknown = response?.ok === true ? await response.json().catch(() => null) : null;
      const parsed = ruleVocabularySchema.safeParse((body as { data?: unknown } | null)?.data);
      if (live.current) {
        setVocabulary(
          parsed.success ? parsed.data : { components: [], fixVersions: [], epics: [] },
        );
      }
    })();
    return () => {
      live.current = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); the pair and the gate are what change. */
  }, [instanceId, remoteProjectId, enabled]);

  return vocabulary;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ P2-32 · Szinkronizálás — which tracker project this line item collects.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * L4 of `docs/plan/line-item-sync.md`. An integration instance, then a remote project reachable
 * through it; the rule below narrows that project's issues. Choosing a target is what makes a line
 * item collect tasks from a tracker, and it is the only place that decision is made — the project
 * form no longer picks an integration (P2-32.1).
 *
 * ⚠️⚠️ **NOT DRAWN BY THE MOCKUP.** The mockup has no per-line-item targeting at all; the layout
 * follows the rule editor directly beneath it — the same 30px controls, the same 11.5px muted
 * labels — so the two read as one section, per `docs/design-system/` deciding what the mockup does
 * not draw.
 *
 * ⚠️⚠️ **THE INSTANCE AND THE PROJECT WAIT FOR *MENTÉS* — 21 Oct 2026.** They saved on every pick,
 * and a target change re-routes: browsing four remote projects left all four projects' tasks on the
 * line item (a task its rule stops claiming keeps its line item, P2-32.6 / D4), so the count grew
 * with every look. The product owner: *"it shouldn't immediately fire when it's selected."* The
 * writes switch and the issue type still save at once — they move no task.
 *
 * ⚠️⚠️ **AND THE FILTERS COME BEFORE *MENTÉS* TOO — 4 Nov 2026.** They used to appear only once
 * the target was saved, and saving it had already routed every synced issue of the project onto
 * the line item under an empty rule. Now a project picked and not saved shows the rule editor,
 * fed from THAT project's Jira lists, and *Mentés* sends the target, rule mode and the rule in one
 * write — so the first routing pass is the filtered one. The saved rule editor below the section
 * steps aside meanwhile (`onDraftChange`), so there is one rule on screen, not two.
 *
 * ⚠️ **Hidden when the workspace has no integration and the line item has no target** — the ruling
 * is "if there are any configured", and an empty picker offering nothing is a control with no use.
 */
export function LineItemTargetSection({
  target,
  options,
  canManage,
  onChange,
  rule,
  ruleOptions,
  onDraftChange,
}: {
  readonly target: LineItemTarget | null;
  readonly options: readonly LineItemTargetOption[];
  readonly canManage: boolean;
  /** `rule` is sent only with a NEW target — the filters written before it was saved. */
  readonly onChange: (target: SetLineItemTarget | null, rule?: LineItemRule) => void;
  /** The line item's saved rule — where a draft rule starts from. */
  readonly rule: LineItemRule;
  readonly ruleOptions: RuleOptions;
  /** Whether a project is picked and not saved — the caller hides its own rule editor then. */
  readonly onDraftChange?: (picking: boolean) => void;
}) {
  const t = useT();
  /* ⚠️ Asked only by somebody who may change it — the endpoint is `lineitem.manage`, and a reader
     gets the sentence below instead of a picker they cannot use. */
  const issueTypes = useIssueTypes(target, canManage);
  /* The picked instance and project, until *Mentés* — `''` for none. */
  const savedInstance = target?.instanceId ?? '';
  const savedProject = target?.remoteProjectId ?? '';
  const [draft, setDraft] = useState({ instanceId: savedInstance, remoteProjectId: savedProject });
  useEffect(() => {
    setDraft({ instanceId: savedInstance, remoteProjectId: savedProject });
  }, [savedInstance, savedProject]);
  const dirty = draft.instanceId !== savedInstance || draft.remoteProjectId !== savedProject;
  /* A NEW target to collect from — not a clear (no instance), which routes nothing in. */
  const picking = dirty && canManage && draft.remoteProjectId !== '';
  const [draftRule, setDraftRule] = useState<LineItemRule>(rule);
  useEffect(() => {
    if (picking) setDraftRule(rule);
    onDraftChange?.(picking);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- on the pick starting or ending; the
       saved rule is where a draft STARTS, not something it follows. */
  }, [picking]);
  const vocabulary = usePickedVocabulary(draft.instanceId, draft.remoteProjectId, picking);
  if (options.length === 0 && target === null) return null;

  const instance =
    options.find((option) => option.instanceId === draft.instanceId) ??
    (target !== null && draft.instanceId === target.instanceId
      ? {
          /* ⚠️ A target on an instance that is no longer active still shows what it names. */
          instanceId: target.instanceId,
          instanceName: target.instanceName,
          extensionId: target.extensionId,
          remoteProjects: [],
        }
      : null);

  const remoteProjects = instance?.remoteProjects ?? [];
  const projectChoices = remoteProjects.map((project) => ({
    value: project.remoteProjectId,
    label: project.reachable
      ? `${project.key} · ${project.name}`
      : `${project.key} · ${project.name} (${t('lineItem.target.unreachable')})`,
    /* ⚠️ Shown and unpickable: a project the credential cannot see would sync nothing. */
    disabled: !project.reachable && project.remoteProjectId !== target?.remoteProjectId,
  }));
  if (
    target !== null &&
    instance?.instanceId === target.instanceId &&
    !remoteProjects.some((project) => project.remoteProjectId === target.remoteProjectId)
  ) {
    projectChoices.unshift({
      value: target.remoteProjectId,
      label: `${target.remoteKey} · ${target.remoteName}`,
      disabled: false,
    });
  }

  /* ⚠️ 29 Oct 2026 · ONE FIELD of the *Szerkesztés* panel (`setting-row.tsx`): the instance, by its
     tracker's LOGO rather than the word *Jira*, and its project side by side — stacked on a phone —
     with the save pair under them while something is only picked. *Szinkronizálás most* is on the
     summary line, where it can be pressed without opening this. */
  return (
    <PanelField label={t('lineItem.target.panelLabel')}>
      <div className="@container">
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-1.5 @max-[380px]:grid-cols-1">
          <Select
            aria-label={t('lineItem.target.instance')}
            className="h-[30px] text-[12.5px]"
            disabled={!canManage}
            value={draft.instanceId}
            options={[
              { value: '', label: t('lineItem.target.noInstance') },
              ...options.map((option) => ({
                value: option.instanceId,
                label: option.instanceName,
                media: <ProviderLogo provider={option.extensionId} size={13} />,
              })),
              /* A target on an instance no longer offered still names itself. */
              ...(target !== null &&
              !options.some((option) => option.instanceId === target.instanceId)
                ? [
                    {
                      value: target.instanceId,
                      label: target.instanceName,
                      media: <ProviderLogo provider={target.extensionId} size={13} />,
                    },
                  ]
                : []),
            ]}
            onChange={(event) => {
              const next = event.target.value;
              if (next === draft.instanceId) return;
              /* The instance's first reachable project is offered; *Mentés* is what commits it. */
              const first = options
                .find((option) => option.instanceId === next)
                ?.remoteProjects.find((project) => project.reachable);
              setDraft({ instanceId: next, remoteProjectId: first?.remoteProjectId ?? '' });
            }}
          />
          {instance === null ? null : (
            <Select
              aria-label={t('lineItem.target.project')}
              className="h-[30px] text-[12.5px]"
              disabled={!canManage}
              searchable
              searchPlaceholder={t('rule.searchValues')}
              emptyLabel={t('lineItem.target.noProjects')}
              value={draft.remoteProjectId}
              options={projectChoices}
              onChange={(event) => {
                setDraft({ instanceId: instance.instanceId, remoteProjectId: event.target.value });
              }}
            />
          )}
        </div>
      </div>
      {picking ? (
        <>
          <p className="text-muted-foreground text-[11.5px]">{t('lineItem.target.filtersFirst')}</p>
          <RuleEditor
            rule={draftRule}
            options={{
              ...ruleOptions,
              components: vocabulary?.components ?? [],
              fixVersions: vocabulary?.fixVersions ?? [],
              epics: vocabulary?.epics ?? [],
            }}
            remote
            immediate
            canManage={canManage}
            excluded={[]}
            onChange={setDraftRule}
            onRestore={() => undefined}
          />
        </>
      ) : null}
      {dirty && canManage ? (
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            /* An instance with no project chosen names nothing to collect from. */
            disabled={draft.instanceId !== '' && draft.remoteProjectId === ''}
            onClick={() => {
              if (draft.instanceId === '') {
                onChange(null);
                return;
              }
              onChange(
                { instanceId: draft.instanceId, remoteProjectId: draft.remoteProjectId },
                draftRule,
              );
            }}
          >
            {t('common.save')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDraft({ instanceId: savedInstance, remoteProjectId: savedProject });
            }}
          >
            {t('common.cancel')}
          </Button>
        </div>
      ) : null}

      {/* The saved target's own settings — hidden while another one is only picked. */}
      {target === null || dirty ? null : (
        <>
          {target.reachable ? null : (
            <p className="flex items-start gap-1.5 text-[11.5px] text-amber-700 dark:text-amber-400">
              <Icon name="triangle-alert" size={13} className="mt-0.5 shrink-0" />
              {t('lineItem.target.unreachableBody', { key: target.remoteKey })}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <label className="flex items-center gap-2 text-[12px]">
              <Switch
                checked={target.writesEnabled}
                disabled={!canManage}
                label={t('lineItem.target.writes')}
                onCheckedChange={(writesEnabled) => {
                  onChange({
                    instanceId: target.instanceId,
                    remoteProjectId: target.remoteProjectId,
                    writesEnabled,
                  });
                }}
              />
              {t('lineItem.target.writes')}
            </label>
            {canManage ? (
              <IssueTypePicker target={target} types={issueTypes} onChange={onChange} />
            ) : (
              <span className="text-muted-foreground text-[12px]">
                {target.issueTypeName === null
                  ? t('lineItem.target.noIssueType')
                  : t('lineItem.target.issueType', { type: target.issueTypeName })}
              </span>
            )}
          </div>
          {target.issueTypeName !== null && EPIC.test(target.issueTypeName) ? (
            <span className="text-muted-foreground text-[11.5px]">
              {t('lineItem.target.epicHint')}
            </span>
          ) : null}
        </>
      )}
    </PanelField>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · what type an issue written to the tracker gets.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Also make the task type (epic) selectable, the user should be able to define what type the
 * task will be in the issue tracker."* The API always took a choice (`setLineItemTargetSchema`);
 * the first sync's `chooseIssueType` only fills a line item that has none, so a choice made here
 * stands.
 *
 * ⚠️ **Sub-tasks are drawn disabled, with their reason**, not dropped: Jira refuses a sub-task
 * created without a parent, and an outbound task has none.
 *
 * ⚠️ **The stored type stays an option even when the list lacks it** — a type the project has since
 * removed, or a list that failed to load — so the picker never shows a saved choice as empty.
 */
function IssueTypePicker({
  target,
  types,
  onChange,
}: {
  readonly target: LineItemTarget;
  readonly types: readonly RemoteIssueType[] | null | 'failed';
  readonly onChange: (target: SetLineItemTarget | null) => void;
}) {
  const t = useT();
  const listed: readonly RemoteIssueType[] = types === null || types === 'failed' ? [] : types;
  const choices = listed.map((type) => ({
    value: type.id,
    label: type.subtask ? t('lineItem.target.issueTypeSubtask', { type: type.name }) : type.name,
    disabled: type.subtask && type.id !== target.issueTypeId,
  }));
  if (target.issueTypeId !== null && !listed.some((type) => type.id === target.issueTypeId)) {
    choices.unshift({
      value: target.issueTypeId,
      label: target.issueTypeName ?? target.issueTypeId,
      disabled: false,
    });
  }

  return (
    <span className="flex items-center gap-1.5 text-[12px]">
      <span className="text-muted-foreground">{t('lineItem.target.issueTypeLabel')}</span>
      <Select
        aria-label={t('lineItem.target.issueTypeLabel')}
        className="h-[30px] w-[180px] text-[12px]"
        searchable
        searchPlaceholder={t('rule.searchValues')}
        emptyLabel={
          types === 'failed'
            ? t('lineItem.target.issueTypesFailed')
            : types === null
              ? t('lineItem.target.issueTypesLoading')
              : t('lineItem.target.noIssueTypes')
        }
        value={target.issueTypeId ?? ''}
        options={[
          ...(target.issueTypeId === null
            ? [{ value: '', label: t('lineItem.target.issueTypeAuto'), disabled: true }]
            : []),
          ...choices,
        ]}
        onChange={(event) => {
          const chosen = listed.find((type) => type.id === event.target.value);
          if (chosen === undefined || chosen.id === target.issueTypeId) return;
          onChange({
            instanceId: target.instanceId,
            remoteProjectId: target.remoteProjectId,
            issueTypeId: chosen.id,
            issueTypeName: chosen.name,
          });
        }}
      />
      {types === 'failed' ? (
        <span className="text-muted-foreground">{t('lineItem.target.issueTypesFailed')}</span>
      ) : null}
    </span>
  );
}
